/**
 * Library Service
 * 
 * Service for interacting with the library backend API.
 * Handles CRUD operations, search, import/export, and usage tracking.
 */

import { config } from '../config/env';

export class LibraryService {
  constructor(baseUrl) {
    this.baseUrl = baseUrl || `${config.BACKEND_URL}/api/v1/library`;
  }

  /**
   * Get all library items with optional filters
   * @param {string} entityType - Optional entity type filter
   * @param {Array<string>} tags - Optional tags filter
   * @param {string} sortBy - Field to sort by
   * @param {number} sortOrder - Sort order (1 or -1)
   * @returns {Promise<Object>} List response with items and count
   */
  async getItems(entityType = null, tags = null, sortBy = 'updated_at', sortOrder = -1) {
    try {
      const params = new URLSearchParams();
      if (entityType) params.append('entity_type', entityType);
      if (tags && tags.length > 0) params.append('tags', tags.join(','));
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder.toString());

      const response = await fetch(`${this.baseUrl}/items?${params}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('Error getting library items:', error);
      throw error;
    }
  }

  /**
   * Create a new library item
   * @param {Object} itemData - Item data to create
   * @returns {Promise<Object>} Created item
   */
  async createItem(itemData) {
    try {
      const response = await fetch(`${this.baseUrl}/items`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(itemData)
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('Error creating library item:', error);
      throw error;
    }
  }

  /**
   * Get a specific library item by ID
   * @param {string} itemId - Item ID
   * @returns {Promise<Object>} Library item
   */
  async getItemById(itemId) {
    try {
      const response = await fetch(`${this.baseUrl}/items/${itemId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error(`Error getting library item ${itemId}:`, error);
      throw error;
    }
  }

  /**
   * Update a library item
   * @param {string} itemId - Item ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} Updated item
   */
  async updateItem(itemId, updateData) {
    try {
      const response = await fetch(`${this.baseUrl}/items/${itemId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error(`Error updating library item ${itemId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a library item
   * @param {string} itemId - Item ID
   * @returns {Promise<Object>} Delete confirmation
   */
  async deleteItem(itemId) {
    try {
      const response = await fetch(`${this.baseUrl}/items/${itemId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error(`Error deleting library item ${itemId}:`, error);
      throw error;
    }
  }

  /**
   * Increment usage count for a library item
   * @param {string} itemId - Item ID
   * @returns {Promise<Object>} Success response
   */
  /**
   * Search library items
   * @param {string} query - Search query
   * @param {Array<string>} entityTypes - Optional entity types filter
   * @param {Array<string>} tags - Optional tags filter
   * @param {number} minUsage - Optional minimum usage count
   * @returns {Promise<Object>} Search results
   */
  async searchItems(query, entityTypes = null, tags = null, minUsage = null) {
    try {
      const params = new URLSearchParams();
      params.append('query', query);
      if (entityTypes && entityTypes.length > 0) {
        params.append('entity_types', entityTypes.join(','));
      }
      if (tags && tags.length > 0) {
        params.append('tags', tags.join(','));
      }
      if (minUsage !== null) {
        params.append('min_usage', minUsage.toString());
      }

      const response = await fetch(`${this.baseUrl}/search?${params}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('Error searching library items:', error);
      throw error;
    }
  }

  /**
   * Get items by entity type
   * @param {string} entityType - Entity type
   * @returns {Promise<Object>} List of items
   */
  async getItemsByType(entityType) {
    try {
      const response = await fetch(`${this.baseUrl}/${entityType}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error(`Error getting items by type ${entityType}:`, error);
      throw error;
    }
  }

  /**
   * Get count of items by entity type
   * @param {string} entityType - Entity type
   * @returns {Promise<Object>} Count response
   */
  async getCountByType(entityType) {
    try {
      const response = await fetch(`${this.baseUrl}/${entityType}/count`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error(`Error getting count for type ${entityType}:`, error);
      throw error;
    }
  }

  /**
   * Get all unique tags
   * @returns {Promise<Object>} Tags list
   */
  async getAllTags() {
    try {
      const response = await fetch(`${this.baseUrl}/tags/all`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('Error getting all tags:', error);
      throw error;
    }
  }

  /**
   * Get item references (what it references and what references it)
   * @param {string} itemId - Item ID
   * @returns {Promise<Object>} References information
   */
  async getItemReferences(itemId) {
    try {
      const response = await fetch(`${this.baseUrl}/items/${itemId}/references`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error(`Error getting references for item ${itemId}:`, error);
      throw error;
    }
  }

  /**
   * Export library items
   * @param {Array<string>} entityTypes - Entity types to export
   * @param {string} format - Export format ('json' or 'csv')
   * @returns {Promise<Object>} Export data
   */
  async exportLibrary(entityTypes, format = 'json') {
    try {
      const response = await fetch(`${this.baseUrl}/export`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          entity_types: entityTypes,
          format: format
        })
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('Error exporting library:', error);
      throw error;
    }
  }

  /**
   * Import library items
   * @param {Array<Object>} items - Items to import
   * @param {string} mode - Import mode ('add', 'overwrite', or 'merge')
   * @returns {Promise<Object>} Import results
   */
  async importLibrary(items, mode = 'add') {
    try {
      const response = await fetch(`${this.baseUrl}/import`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: items,
          mode: mode
        })
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('Error importing library:', error);
      throw error;
    }
  }

  /**
   * Get library statistics
   * @returns {Promise<Object>} Library statistics
   */
  async getStats() {
    try {
      const response = await fetch(`${this.baseUrl}/stats`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('Error getting library stats:', error);
      throw error;
    }
  }

  /**
   * Handle API response
   * @param {Response} response - Fetch response
   * @returns {Promise<Object>} Parsed JSON response
   * @throws {Error} If response is not OK
   */
  async handleResponse(response) {
    if (!response.ok) {
      let errorMessage = `Request failed with status ${response.status}`;
      
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.message || errorMessage;
      } catch (e) {
        // If error response is not JSON, use status text
        errorMessage = response.statusText || errorMessage;
      }

      throw new Error(errorMessage);
    }

    try {
      return await response.json();
    } catch (e) {
      // If response has no body, return empty object
      return {};
    }
  }

  async getThreatCatalogue() {
    const response = await fetch(`${this.baseUrl}/catalogue/threats`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return this.handleResponse(response);
  }
}

// Export singleton instance
export const libraryService = new LibraryService();

export default libraryService;
